import { useState, useEffect, useCallback, useMemo } from 'react';

type Validator<T, K extends keyof T = keyof T> = (
  value?: T[K],
  allValues?: Partial<T>,
) => (string | undefined) | Promise<string | undefined>;

export const useForm = <T extends Record<keyof T, unknown>>(
  allValues: Partial<T>,
  fieldValidators: Record<keyof T, Validator<T>[]>,
) => {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isInitialValidationDone, setIsInitialValidationDone] =
    useState<boolean>(false);

  useEffect(() => {
    const validateForm = async () => {
      const initialErrors: Partial<Record<keyof T, string>> = {};

      for (const [fieldName, validators] of Object.entries(fieldValidators)) {
        for (const validate of validators as Validator<T>[]) {
          const result = validate(allValues[fieldName as keyof T], allValues);
          const error = result instanceof Promise ? await result : result;
          if (error) {
            initialErrors[fieldName as keyof T] = error;
            break;
          }
        }
      }
      setErrors(initialErrors);
    };
    if (!isInitialValidationDone) {
      validateForm();
      setIsInitialValidationDone(true);
    }
  }, [allValues, fieldValidators, errors, setErrors, isInitialValidationDone]);

  const validateField = useCallback(
    async <K extends keyof T>(fieldName: K, value?: T[K]) => {
      const validators = fieldValidators[fieldName] || [];

      for (const validate of validators) {
        const result = validate(value, allValues);
        const error = result instanceof Promise ? await result : result;
        if (error) {
          setErrors({ ...errors, [fieldName]: error });
          return error;
        }
      }
      setErrors({ ...errors, [fieldName]: '' });
    },
    [allValues, fieldValidators, errors],
  );

  const hasValidationErrors = useMemo(() => {
    return (
      !isInitialValidationDone || Object.values(errors).some((error) => !!error)
    );
  }, [isInitialValidationDone, errors]);

  const showError = (fieldName: keyof T) => {
    setTouched({ ...touched, [fieldName]: true });
  };

  const handleBlur = (fieldName: keyof T) => async () => {
    const error = await validateField(fieldName, allValues[fieldName]);
    setErrors({ ...errors, [fieldName]: error || null });
    showError(fieldName);
  };

  return {
    errors,
    touched,
    validateField,
    hasValidationErrors,
    showError,
    handleBlur,
  };
};
